import React, { useState, useEffect } from 'react';

import Api from '../../services/api.service';

import Loading from '../../components/Loading/loading';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import TitlePage from '../../components/title-page';

import { useCart } from '../../contexts/cart-context';
import StepCheckCart from './step-check-cart';
import StepPersonnalsInfos from './step-personnals-infos';
import StepPaymentCart from './step-payment-cart';
import StepConfirmationCart from './step-confirmation-cart';

export default function Cart() {
    const { cart, updateCart, removeFromCart, clearCart } = useCart();

    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [step, setStep] = useState('check');

    // ➕ Infos pour l’étape paiement
    const [orderInfos, setOrderInfos] = useState(null);

    const { t } = useTranslation();

    useEffect(() => {
        if (!cart.length) {
            setProducts([]);
            setLoading(false);
            return;
        }

        setLoading(true);

        Api.getCart(cart).then((response) => {
            const res = response.data;
            if (res.status) {
                const serverProducts = res.data || [];
                setProducts(serverProducts);

                syncCartWithServer(cart, serverProducts, { updateCart, removeFromCart });
            } else {
                console.log('error');
            }
            setLoading(false);
        });
    }, [cart, updateCart, removeFromCart]);

    const handleReturnedData = async (data) => {
        if (data === "personnalInfos") {
            setStep("personnalInfos");
            return;
        }

        if (data === "check") {
            setStep("check");
            return;
        }

        // 🔹 On récupère les infos validées depuis StepPersonnalsInfos
        if (data && data.type === "SUBMIT_INFOS") {
            setOrderInfos({
                personnalInfo: data.payload.personnalInfo,
                billingInfo: data.payload.billingInfo,
            });
            setStep("payment");
            return;
        }

        if (data && data.type === "PAID") {
            clearCart();
            setStep("confirmation");
            return;
        }
    };

    return (
        <div className="bg-backgroundColor">
            <Helmet>
                <title>L&apos;Artbanne - {t('cart.pageTitle')}</title>
            </Helmet>

            <div className="m-auto max-w-6xl w-5/6 pt-14">
                <TitlePage
                    title={t('cart.welcomeMessage')}
                    description={t('cart.description')}
                />

                <div className="my-12">
                    {loading ? (
                        <Loading />
                    ) : (
                        <>
                            {step === 'check' && (
                                <StepCheckCart
                                    products={products}
                                    returnedData={handleReturnedData}
                                />
                            )}

                            {step === 'personnalInfos' && (
                                <StepPersonnalsInfos
                                    returnedData={handleReturnedData}
                                />
                            )}

                            {step === 'payment' && orderInfos && (
                                <StepPaymentCart
                                    orderInfos={orderInfos}
                                    returnedData={handleReturnedData}
                                />
                            )}

                            {step === 'confirmation' && (
                                <StepConfirmationCart
                                    returnedData={handleReturnedData}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

/**
 * Compare local cart with server data:
 * - If requested qty > stock, clamp it (or remove if stock = 0)
 * - Prices are NOT read from cart, we trust only server `products`.
 */
function syncCartWithServer(cart, serverProducts, { updateCart, removeFromCart }) {
    cart.forEach((line) => {
        const product = serverProducts.find((p) => p.slug === line.slug);
        if (!product || !product.modelProduct || !product.modelProduct.length) {
            removeFromCart({ slug: line.slug, reference: line.reference });
            return;
        }

        const model =
            product.modelProduct.find((m) => m.reference === line.reference) ||
            product.modelProduct[0];

        const stock = Number(model.quantity) || 0;
        const currentQty = Number(line.quantity) || 0;

        if (stock <= 0) {
            removeFromCart({ slug: line.slug, reference: model.reference });
            return;
        }

        if (currentQty > stock) {
            updateCart({
                slug: line.slug,
                reference: model.reference,
                quantity: stock,
            });
        }
    });
}